import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Futuristic Task Progress Tracker (Jira/ClickUp-style) — Single JSX File
 * - Dashboard (cards + charts + activity)
 * - Kanban (drag & drop, customizable columns)
 * - Table (search/filter/sort, inline edit, bulk actions)
 * - Notes (per task + global project notes + brain dump -> tasks)
 * - Calendar (month view + weekly list, drag tasks onto dates, day drawer)
 *
 * Assumptions:
 * - TailwindCSS is available in your project (recommended).
 * - Framer Motion installed: `npm i framer-motion`
 *
 * No backend required (local/mock state). Copy-paste as a single file.
 */

export default function TaskProgressTrackerApp() {
  /* --------------------------------- Config -------------------------------- */
  const APP_NAME = "Roadmap Tracker";
  const PROJECT_NAME = "InterviewGenieAI";
  const STATUSES = ["backlog", "planned", "in_progress", "review", "done"];
  const STATUS_LABEL = {
    backlog: "Backlog",
    planned: "Planned",
    in_progress: "In Progress",
    review: "Review",
    done: "Done",
    blocked: "Blocked", // special flag (not a column by default)
  };
  const PRIORITIES = ["low", "medium", "high", "critical"];
  const PRIORITY_LABEL = {
    low: "Low",
    medium: "Medium",
    high: "High",
    critical: "Critical",
  };

  /* --------------------------------- Helpers -------------------------------- */
  const uid = () => Math.random().toString(16).slice(2) + Date.now().toString(16);
  const pad2 = (n) => String(n).padStart(2, "0");
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };
  const parseISODate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
  };
  const isOverdue = (task) => {
    if (!task?.dueDate) return false;
    if (task?.status === "done") return false;
    const d = parseISODate(task.dueDate);
    if (!d) return false;
    const now = new Date();
    // compare date-only
    const a = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const b = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return b < a;
  };
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const formatNiceDate = (iso) => {
    const d = parseISODate(iso);
    if (!d) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const startOfWeek = (date, weekStartsOnMonday = true) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 Sun .. 6 Sat
    const shift = weekStartsOnMonday ? (day === 0 ? 6 : day - 1) : day;
    d.setDate(d.getDate() - shift);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const addDays = (date, n) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  };
  const isoFromDate = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

  /* ------------------------------- Mock Data -------------------------------- */
  const initialColumns = [
    { id: "backlog", title: "Backlog" },
    { id: "planned", title: "Planned" },
    { id: "in_progress", title: "In Progress" },
    { id: "review", title: "Review" },
    { id: "done", title: "Done" },
  ];

  const seedTasks = () => {
    const t = todayISO();
    const d = parseISODate(t);
    const plus = (n) => isoFromDate(addDays(d, n));
    return [
      {
        id: uid(),
        title: "Finalize InterviewGenieAI roadmap v1",
        status: "planned",
        priority: "high",
        tags: ["strategy", "milestone"],
        dueDate: plus(3),
        assignee: "",
        blocked: false,
        notes: "Define MVP + next 2 sprints. Include calendar + notifications.",
        createdAt: Date.now() - 1000 * 60 * 60 * 48,
        updatedAt: Date.now() - 1000 * 60 * 60 * 4,
      },
      {
        id: uid(),
        title: "Implement AI Interview scheduling calendar UI",
        status: "in_progress",
        priority: "critical",
        tags: ["calendar", "frontend"],
        dueDate: plus(7),
        assignee: "",
        blocked: false,
        notes: "Month view + drag tasks onto dates. Add day drawer.",
        createdAt: Date.now() - 1000 * 60 * 60 * 72,
        updatedAt: Date.now() - 1000 * 60 * 15,
      },
      {
        id: uid(),
        title: "Notifications: web push + email reminder flows",
        status: "backlog",
        priority: "medium",
        tags: ["notifications", "backend"],
        dueDate: plus(14),
        assignee: "",
        blocked: false,
        notes: "Celery Beat reminders + Web Push from browser (frontend).",
        createdAt: Date.now() - 1000 * 60 * 60 * 90,
        updatedAt: Date.now() - 1000 * 60 * 60 * 10,
      },
      {
        id: uid(),
        title: "Refactor question generation caching rules",
        status: "review",
        priority: "high",
        tags: ["ai", "redis"],
        dueDate: plus(1),
        assignee: "",
        blocked: true,
        notes: "Audit cache keys: role+level+language+persona. Add rate-limit audit log.",
        createdAt: Date.now() - 1000 * 60 * 60 * 120,
        updatedAt: Date.now() - 1000 * 60 * 60 * 2,
      },
      {
        id: uid(),
        title: "Landing page polish ($10M design pass)",
        status: "done",
        priority: "medium",
        tags: ["design", "marketing"],
        dueDate: plus(-2),
        assignee: "",
        blocked: false,
        notes: "Keep TechApp palette. Improve hero + transitions.",
        createdAt: Date.now() - 1000 * 60 * 60 * 160,
        updatedAt: Date.now() - 1000 * 60 * 60 * 20,
      },
      {
        id: uid(),
        title: "Video interview analyzer UX improvements",
        status: "planned",
        priority: "high",
        tags: ["ux", "video"],
        dueDate: plus(10),
        assignee: "",
        blocked: false,
        notes: "Smooth opening, heavier dark theme, add Framer Motion.",
        createdAt: Date.now() - 1000 * 60 * 60 * 40,
        updatedAt: Date.now() - 1000 * 60 * 60 * 6,
      },
    ];
  };

  /* --------------------------------- State ---------------------------------- */
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard"); // dashboard | kanban | table | notes | calendar
  const [columns, setColumns] = useState(initialColumns);
  const [tasks, setTasks] = useState(seedTasks);
  const [projectNotes, setProjectNotes] = useState(
    "Project Notes\n\n- Keep everything simple, fast, and visually premium.\n- Weekly milestones + daily execution.\n"
  );
  const [activity, setActivity] = useState(() => [
    {
      id: uid(),
      ts: Date.now() - 1000 * 60 * 45,
      type: "system",
      message: `Welcome — ${PROJECT_NAME} roadmap loaded.`,
    },
  ]);

  // Global UI
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [toast, setToast] = useState(null);

  // Table controls
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [sortKey, setSortKey] = useState("updatedAt");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(() => new Set());

  // Notes + focused task
  const [activeTaskId, setActiveTaskId] = useState(null);

  // Kanban drag
  const [draggingId, setDraggingId] = useState(null);

  // Calendar
  const [calAnchor, setCalAnchor] = useState(() => new Date());
  const [dayDrawer, setDayDrawer] = useState({ open: false, iso: null });

  // Brain dump
  const [brainDump, setBrainDump] = useState("");

  const searchRef = useRef(null);
  const newTaskTitleRef = useRef(null);

  /* ------------------------------- Boot Loading ------------------------------ */
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 450);
    return () => clearTimeout(t);
  }, []);

  /* ------------------------------- Derived Data ------------------------------ */
  const tasksById = useMemo(() => {
    const m = new Map();
    for (const t of tasks) m.set(t.id, t);
    return m;
  }, [tasks]);

  const activeTask = useMemo(() => (activeTaskId ? tasksById.get(activeTaskId) : null), [activeTaskId, tasksById]);

  const metrics = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter((t) => t.status === "done").length;
    const inProgress = tasks.filter((t) => t.status === "in_progress").length;
    const blocked = tasks.filter((t) => t.blocked).length;
    const overdue = tasks.filter((t) => isOverdue(t)).length;

    const statusCounts = STATUSES.reduce((acc, s) => {
      acc[s] = tasks.filter((t) => t.status === s).length;
      return acc;
    }, {});
    return { total, done, inProgress, blocked, overdue, statusCounts };
  }, [tasks]);

  const completionSeries = useMemo(() => {
    // last 14 days done count cumulative (simple)
    const days = 14;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const pts = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = addDays(start, -i);
      const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
      const doneBy = tasks.filter((t) => t.status === "done" && (t.updatedAt || 0) <= dayEnd).length;
      pts.push({ x: isoFromDate(d), y: doneBy });
    }
    return pts;
  }, [tasks]);

  const filteredSortedTasks = useMemo(() => {
    let list = [...tasks];

    // search
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((t) => {
        const hay = `${t.title} ${(t.tags || []).join(" ")} ${t.assignee || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }

    // filters
    if (statusFilter !== "all") list = list.filter((t) => t.status === statusFilter);
    if (priorityFilter !== "all") list = list.filter((t) => t.priority === priorityFilter);
    const tagQ = tagFilter.trim().toLowerCase();
    if (tagQ) list = list.filter((t) => (t.tags || []).some((x) => x.toLowerCase().includes(tagQ)));

    // sorting
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });

    return list;
  }, [tasks, query, statusFilter, priorityFilter, tagFilter, sortKey, sortDir]);

  const weekRows = useMemo(() => {
    // For the month view, generate visible grid weeks
    const mStart = startOfMonth(calAnchor);
    const mEnd = endOfMonth(calAnchor);
    const gridStart = startOfWeek(mStart, true);
    const gridEnd = addDays(startOfWeek(addDays(mEnd, 6), true), 6); // ensure full last week
    const days = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return { weeks, mStart, mEnd };
  }, [calAnchor]);

  const tasksByDate = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      if (!t.dueDate) continue;
      if (!map.has(t.dueDate)) map.set(t.dueDate, []);
      map.get(t.dueDate).push(t);
    }
    // stable sort inside dates
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      map.set(k, list);
    }
    return map;
  }, [tasks]);

  /* --------------------------------- Actions -------------------------------- */
  const pushActivity = (type, message) => {
    setActivity((prev) => [{ id: uid(), ts: Date.now(), type, message }, ...prev].slice(0, 60));
  };

  const showToast = (message) => {
    setToast({ id: uid(), message });
    setTimeout(() => setToast(null), 2200);
  };

  const createTask = (partial) => {
    const now = Date.now();
    const t = {
      id: uid(),
      title: partial?.title?.trim() || "Untitled task",
      status: partial?.status || "backlog",
      priority: partial?.priority || "medium",
      tags: Array.isArray(partial?.tags) ? partial.tags : [],
      dueDate: partial?.dueDate || "",
      assignee: partial?.assignee || "",
      blocked: !!partial?.blocked,
      notes: partial?.notes || "",
      createdAt: now,
      updatedAt: now,
    };
    setTasks((prev) => [t, ...prev]);
    pushActivity("create", `Created: “${t.title}”`);
    showToast("Task created");
    return t.id;
  };

  const updateTask = (id, patch, activityMsg) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const next = { ...t, ...patch, updatedAt: Date.now() };
        return next;
      })
    );
    if (activityMsg) pushActivity("update", activityMsg);
  };

  const deleteTask = (id) => {
    const t = tasksById.get(id);
    setTasks((prev) => prev.filter((x) => x.id !== id));
    setSelected((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    if (activeTaskId === id) setActiveTaskId(null);
    pushActivity("delete", `Deleted: “${t?.title || "task"}”`);
    showToast("Task deleted");
  };

  const bulkUpdate = (ids, patch, label) => {
    const count = ids.length;
    if (!count) return;
    setTasks((prev) =>
      prev.map((t) => (ids.includes(t.id) ? { ...t, ...patch, updatedAt: Date.now() } : t))
    );
    setSelected(new Set());
    pushActivity("bulk", `${label} (${count})`);
    showToast(`${label} (${count})`);
  };

  const brainDumpToTasks = () => {
    const raw = brainDump.trim();
    if (!raw) return;
    const lines = raw
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    const createdIds = [];
    for (const line of lines) {
      // Mini DSL:
      // - tags: #tag
      // - priority: !low !med !high !crit
      // - due date: @2026-02-21 (ISO)
      // - status: :backlog :planned :in_progress :review :done
      // - blocked: [blocked]
      const tags = Array.from(line.matchAll(/#([\w-]+)/g)).map((m) => m[1]);
      const due = (line.match(/@(\d{4}-\d{2}-\d{2})/) || [])[1] || "";
      const status = (line.match(/:(backlog|planned|in_progress|review|done)/) || [])[1] || "backlog";
      const blocked = /\[blocked\]/i.test(line);

      let priority = "medium";
      if (/!crit/i.test(line)) priority = "critical";
      else if (/!high/i.test(line)) priority = "high";
      else if (/!low/i.test(line)) priority = "low";
      else if (/!med/i.test(line)) priority = "medium";

      const title = line
        .replace(/#([\w-]+)/g, "")
        .replace(/@(\d{4}-\d{2}-\d{2})/g, "")
        .replace(/:(backlog|planned|in_progress|review|done)/g, "")
        .replace(/\[blocked\]/gi, "")
        .replace(/!(crit|high|med|low)/gi, "")
        .trim();

      const id = createTask({ title: title || "New task", tags, dueDate: due, status, priority, blocked });
      createdIds.push(id);
    }

    setBrainDump("");
    showToast(`Converted to ${createdIds.length} task(s)`);
  };

  /* ----------------------------- Keyboard Shortcuts -------------------------- */
  useEffect(() => {
    const onKeyDown = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const mod = isMac ? e.metaKey : e.ctrlKey;

      // Ignore if typing in inputs/textarea
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target?.isContentEditable;

      // Basic shortcuts
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }

      if (!typing && e.key.toLowerCase() === "n") {
        e.preventDefault();
        // quick create
        const id = createTask({ title: "New task", status: "backlog", priority: "medium" });
        setActiveTaskId(id);
        setView("notes");
        setTimeout(() => newTaskTitleRef.current?.focus?.(), 80);
      }

      // View switching: g then key
      // Simple: Ctrl/Cmd + 1..5
      if (mod && !typing) {
        if (e.key === "1") setView("dashboard");
        if (e.key === "2") setView("kanban");
        if (e.key === "3") setView("table");
        if (e.key === "4") setView("notes");
        if (e.key === "5") setView("calendar");
      }

      // Esc closes day drawer
      if (e.key === "Escape") {
        setDayDrawer({ open: false, iso: null });
        setDraggingId(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brainDump, view, tasks]);

  /* -------------------------- Drag & Drop (Kanban) --------------------------- */
  const onDragStartTask = (taskId) => {
    setDraggingId(taskId);
  };

  const onDropToStatus = (statusId) => {
    if (!draggingId) return;
    const t = tasksById.get(draggingId);
    if (!t) return;
    if (t.status === statusId) {
      setDraggingId(null);
      return;
    }
    updateTask(draggingId, { status: statusId }, `Moved “${t.title}” → ${STATUS_LABEL[statusId]}`);
    setDraggingId(null);
  };

  /* --------------------------- Drag & Drop (Calendar) ------------------------ */
  const onDropToDate = (iso) => {
    if (!draggingId) return;
    const t = tasksById.get(draggingId);
    if (!t) return;
    updateTask(draggingId, { dueDate: iso }, `Scheduled “${t.title}” → ${iso}`);
    setDraggingId(null);
  };

  /* --------------------------- Column Customization -------------------------- */
  const addColumn = () => {
    const name = prompt("Column name?");
    if (!name) return;
    const id = name
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^\w_]/g, "")
      .slice(0, 24);
    if (!id) return;
    if (columns.some((c) => c.id === id)) {
      showToast("Column already exists");
      return;
    }
    setColumns((prev) => [...prev, { id, title: name }]);
    pushActivity("columns", `Added column: ${name}`);
    showToast("Column added");
  };

  const renameColumn = (colId) => {
    const col = columns.find((c) => c.id === colId);
    const name = prompt("Rename column", col?.title || "");
    if (!name) return;
    setColumns((prev) => prev.map((c) => (c.id === colId ? { ...c, title: name } : c)));
    pushActivity("columns", `Renamed column: ${col?.title || colId} → ${name}`);
    showToast("Column renamed");
  };

  const removeColumn = (colId) => {
    if (!confirm("Remove this column? Tasks in it will move to Backlog.")) return;
    setColumns((prev) => prev.filter((c) => c.id !== colId));
    setTasks((prev) =>
      prev.map((t) => (t.status === colId ? { ...t, status: "backlog", updatedAt: Date.now() } : t))
    );
    pushActivity("columns", `Removed column: ${colId}`);
    showToast("Column removed");
  };

  /* --------------------------------- UI ------------------------------------- */
  if (loading) {
    return (
      <AppShell
        appName={APP_NAME}
        projectName={PROJECT_NAME}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        view={view}
        setView={setView}
        metrics={metrics}
        searchRef={searchRef}
        query={query}
        setQuery={setQuery}
      >
        <div className="p-6">
          <LoadingState />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      appName={APP_NAME}
      projectName={PROJECT_NAME}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      view={view}
      setView={setView}
      metrics={metrics}
      searchRef={searchRef}
      query={query}
      setQuery={setQuery}
    >
      <div className="relative">
        <AnimatePresence>
          {toast && (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              className="fixed right-4 top-4 z-[60]"
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-3 shadow-[0_18px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl">
                <div className="text-sm text-white/90">{toast.message}</div>
                <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
                  <motion.div
                    className="h-full bg-white/40"
                    initial={{ width: "100%" }}
                    animate={{ width: "0%" }}
                    transition={{ duration: 2.1, ease: "linear" }}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {view === "dashboard" && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              className="p-6"
            >
              <Dashboard
                metrics={metrics}
                completionSeries={completionSeries}
                tasks={tasks}
                activity={activity}
                onQuickCreate={() => {
                  const id = createTask({ title: "New task", status: "backlog" });
                  setActiveTaskId(id);
                  setView("notes");
                  setTimeout(() => newTaskTitleRef.current?.focus?.(), 80);
                }}
              />
            </motion.div>
          )}

          {view === "kanban" && (
            <motion.div
              key="kanban"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              className="p-6"
            >
              <KanbanBoard
                columns={columns}
                tasks={tasks}
                draggingId={draggingId}
                onDragStart={onDragStartTask}
                onDropToStatus={onDropToStatus}
                onOpenTask={(id) => {
                  setActiveTaskId(id);
                  setView("notes");
                }}
                onAddColumn={addColumn}
                onRenameColumn={renameColumn}
                onRemoveColumn={removeColumn}
                onToggleBlocked={(id) => {
                  const t = tasksById.get(id);
                  updateTask(id, { blocked: !t.blocked }, `Updated blocked: “${t.title}”`);
                }}
              />
            </motion.div>
          )}

          {view === "table" && (
            <motion.div
              key="table"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              className="p-6"
            >
              <TasksTable
                tasks={filteredSortedTasks}
                columns={columns}
                STATUSES={STATUSES}
                STATUS_LABEL={STATUS_LABEL}
                PRIORITIES={PRIORITIES}
                PRIORITY_LABEL={PRIORITY_LABEL}
                query={query}
                setQuery={setQuery}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                priorityFilter={priorityFilter}
                setPriorityFilter={setPriorityFilter}
                tagFilter={tagFilter}
                setTagFilter={setTagFilter}
                sortKey={sortKey}
                setSortKey={setSortKey}
                sortDir={sortDir}
                setSortDir={setSortDir}
                selected={selected}
                setSelected={setSelected}
                onOpenTask={(id) => {
                  setActiveTaskId(id);
                  setView("notes");
                }}
                onInlineUpdate={(id, patch) => {
                  const t = tasksById.get(id);
                  const changes = Object.keys(patch)
                    .map((k) => `${k}`)
                    .join(", ");
                  updateTask(id, patch, `Edited “${t?.title || "task"}” (${changes})`);
                }}
                onDelete={(id) => deleteTask(id)}
                onBulk={(action) => {
                  const ids = Array.from(selected);
                  if (!ids.length) return;
                  if (action.type === "delete") {
                    if (!confirm(`Delete ${ids.length} task(s)?`)) return;
                    ids.forEach((id) => deleteTask(id));
                    setSelected(new Set());
                    return;
                  }
                  bulkUpdate(ids, action.patch, action.label);
                }}
              />
            </motion.div>
          )}

          {view === "notes" && (
            <motion.div
              key="notes"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              className="p-6"
            >
              <NotesHub
                tasks={tasks}
                activeTask={activeTask}
                setActiveTaskId={setActiveTaskId}
                projectNotes={projectNotes}
                setProjectNotes={(val) => {
                  setProjectNotes(val);
                  pushActivity("notes", "Updated Project Notes");
                }}
                onCreateTask={(title) => {
                  const id = createTask({ title, status: "backlog" });
                  setActiveTaskId(id);
                  return id;
                }}
                onUpdateTask={(id, patch) => {
                  const t = tasksById.get(id);
                  updateTask(id, patch, `Updated notes/details: “${t?.title || "task"}”`);
                }}
                onDeleteTask={(id) => deleteTask(id)}
                brainDump={brainDump}
                setBrainDump={setBrainDump}
                onBrainDumpConvert={brainDumpToTasks}
                newTaskTitleRef={newTaskTitleRef}
              />
            </motion.div>
          )}

          {view === "calendar" && (
            <motion.div
              key="calendar"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              className="p-6"
            >
              <CalendarPlanner
                calAnchor={calAnchor}
                setCalAnchor={setCalAnchor}
                weekRows={weekRows}
                tasksByDate={tasksByDate}
                tasks={tasks}
                draggingId={draggingId}
                onDragStart={onDragStartTask}
                onDropToDate={onDropToDate}
                onOpenDay={(iso) => setDayDrawer({ open: true, iso })}
                onCreateOnDay={(iso) => {
                  const id = createTask({ title: "New scheduled task", dueDate: iso, status: "planned" });
                  setActiveTaskId(id);
                  setView("notes");
                }}
                onOpenTask={(id) => {
                  setActiveTaskId(id);
                  setView("notes");
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <DayDrawer
          open={dayDrawer.open}
          iso={dayDrawer.iso}
          onClose={() => setDayDrawer({ open: false, iso: null })}
          tasks={(dayDrawer.iso && tasksByDate.get(dayDrawer.iso)) || []}
          onOpenTask={(id) => {
            setDayDrawer({ open: false, iso: null });
            setActiveTaskId(id);
            setView("notes");
          }}
          onCreate={() => {
            if (!dayDrawer.iso) return;
            const id = createTask({ title: "New task", dueDate: dayDrawer.iso, status: "planned" });
            setDayDrawer({ open: false, iso: null });
            setActiveTaskId(id);
            setView("notes");
          }}
        />
      </div>
    </AppShell>
  );
}

/* =============================================================================
 * Shell + Layout
 * ============================================================================= */

function AppShell({
  children,
  appName,
  projectName,
  sidebarOpen,
  setSidebarOpen,
  view,
  setView,
  metrics,
  searchRef,
  query,
  setQuery,
}) {
  const tabs = [
    { id: "dashboard", label: "Dashboard", kbd: "Ctrl+1" },
    { id: "kanban", label: "Kanban", kbd: "Ctrl+2" },
    { id: "table", label: "Table", kbd: "Ctrl+3" },
    { id: "notes", label: "Notes", kbd: "Ctrl+4" },
    { id: "calendar", label: "Calendar", kbd: "Ctrl+5" },
  ];

  return (
    <div className="min-h-screen w-full bg-[#070812] text-white">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-28 -left-24 h-[520px] w-[520px] rounded-full bg-fuchsia-500/10 blur-[90px]" />
        <div className="absolute top-20 -right-28 h-[520px] w-[520px] rounded-full bg-cyan-400/10 blur-[90px]" />
        <div className="absolute -bottom-40 left-1/3 h-[620px] w-[620px] rounded-full bg-indigo-500/10 blur-[110px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.06),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:44px_44px]" />
      </div>

      <div className="relative z-10 flex min-h-screen">
        {/* Sidebar */}
        <AnimatePresence initial={false}>
          {sidebarOpen && (
            <motion.aside
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -14, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="hidden lg:flex w-[300px] shrink-0 flex-col p-4"
            >
              <GlassPanel className="h-full p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-white/50">Workspace</div>
                    <div className="mt-1 text-lg font-semibold tracking-tight">{projectName}</div>
                    <div className="mt-1 text-xs text-white/50">{appName}</div>
                  </div>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2 text-xs text-white/80 hover:bg-white/[0.08]"
                  >
                    Hide
                  </button>
                </div>

                <div className="mt-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-white/85">Momentum</div>
                      <div className="text-xs text-white/50">{metrics.done}/{metrics.total} done</div>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-300/70 via-indigo-300/70 to-fuchsia-300/70"
                        style={{ width: `${metrics.total ? Math.round((metrics.done / metrics.total) * 100) : 0}%` }}
                      />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-white/70">
                      <StatMini label="In Progress" value={metrics.inProgress} />
                      <StatMini label="Blocked" value={metrics.blocked} />
                      <StatMini label="Overdue" value={metrics.overdue} />
                      <StatMini label="Total" value={metrics.total} />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-xs text-white/50 mb-2">Navigation</div>
                  <div className="flex flex-col gap-2">
                    {tabs.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setView(t.id)}
                        className={[
                          "group flex items-center justify-between rounded-2xl border px-3 py-2.5 text-sm transition",
                          view === t.id
                            ? "border-white/20 bg-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_18px_60px_rgba(0,0,0,0.35)]"
                            : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
                        ].join(" ")}
                      >
                        <span className="text-white/90">{t.label}</span>
                        <span className="text-[10px] text-white/45 group-hover:text-white/60">{t.kbd}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-xs text-white/50 mb-2">Shortcuts</div>
                  <div className="space-y-2 text-xs text-white/70">
                    <ShortcutRow k="/ " v="Focus search" />
                    <ShortcutRow k="N" v="New task" />
                    <ShortcutRow k="Esc" v="Close drawers" />
                  </div>
                </div>

                <div className="mt-auto pt-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                    <div className="text-xs text-white/55">Tip</div>
                    <div className="mt-1 text-sm text-white/85">
                      Keep tasks tiny. Ship daily. Review weekly.
                    </div>
                  </div>
                </div>
              </GlassPanel>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Main */}
        <div className="flex-1 flex flex-col">
          <TopBar
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            view={view}
            setView={setView}
            searchRef={searchRef}
            query={query}
            setQuery={setQuery}
          />
          <div className="flex-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

function TopBar({ sidebarOpen, setSidebarOpen, view, setView, searchRef, query, setQuery }) {
  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "kanban", label: "Kanban" },
    { id: "table", label: "Table" },
    { id: "notes", label: "Notes" },
    { id: "calendar", label: "Calendar" },
  ];

  return (
    <div className="sticky top-0 z-40 border-b border-white/10 bg-[#070812]/70 backdrop-blur-xl">
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2 text-xs text-white/85 hover:bg-white/[0.08] lg:hidden"
          >
            {sidebarOpen ? "Hide" : "Menu"}
          </button>

          <div className="hidden lg:flex items-center gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setView(t.id)}
                className={[
                  "rounded-2xl border px-3 py-2 text-sm transition",
                  view === t.id
                    ? "border-white/20 bg-white/[0.08] text-white shadow-[0_0_0_1px_rgba(255,255,255,0.05)]"
                    : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.06] hover:text-white/90",
                ].join(" ")}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative w-[320px] max-w-[52vw]">
              <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-white/40">
                <span className="text-sm">⌕</span>
              </div>
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tasks…  (press /)"
                className="w-full rounded-2xl border border-white/10 bg-white/[0.05] pl-9 pr-3 py-2 text-sm text-white/90 placeholder:text-white/35 outline-none focus:border-white/20 focus:bg-white/[0.07]"
              />
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <Pill label="N" hint="New" />
              <Pill label="Ctrl+1..5" hint="Views" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
 * Dashboard
 * ============================================================================= */

function Dashboard({ metrics, completionSeries, tasks, activity, onQuickCreate }) {
  const statusData = useMemo(() => {
    const order = ["backlog", "planned", "in_progress", "review", "done"];
    return order.map((s) => ({ label: s, value: metrics.statusCounts[s] || 0 }));
  }, [metrics]);

  const recentOverdue = useMemo(
    () => tasks.filter((t) => t.dueDate && t.status !== "done").filter((t) => isOverdue(t)).slice(0, 6),
    [tasks]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-xs text-white/50">Overview</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight">Roadmap Momentum</div>
          <div className="mt-1 text-sm text-white/60">Fast glance: what’s next, what’s stuck, what’s done.</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onQuickCreate}
            className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-2 text-sm text-white/90 hover:bg-white/[0.1] shadow-[0_18px_60px_rgba(0,0,0,0.35)]"
          >
            + New task
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <SummaryCard title="Total" value={metrics.total} glow="cyan" />
        <SummaryCard title="Done" value={metrics.done} glow="emerald" />
        <SummaryCard title="In Progress" value={metrics.inProgress} glow="indigo" />
        <SummaryCard title="Blocked" value={metrics.blocked} glow="fuchsia" />
        <SummaryCard title="Overdue" value={metrics.overdue} glow="rose" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <GlassPanel className="p-4 xl:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/85">Completion over time</div>
              <div className="text-xs text-white/50">Last 14 days (cumulative done)</div>
            </div>
            <div className="text-xs text-white/50">Smooth shipping streak</div>
          </div>
          <div className="mt-4">
            <MiniLineChart data={completionSeries} />
          </div>
        </GlassPanel>

        <GlassPanel className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/85">Status distribution</div>
              <div className="text-xs text-white/50">Where work lives right now</div>
            </div>
          </div>
          <div className="mt-4">
            <MiniDonut data={statusData} />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-white/70">
            {statusData.map((s) => (
              <div key={s.label} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1.5">
                <span className="capitalize">{s.label.replace("_", " ")}</span>
                <span className="text-white/85">{s.value}</span>
              </div>
            ))}
          </div>
        </GlassPanel>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <GlassPanel className="p-4 xl:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/85">Activity</div>
              <div className="text-xs text-white/50">Recent updates across the system</div>
            </div>
            <div className="text-xs text-white/45">Auto-capped</div>
          </div>
          <div className="mt-4">
            <ActivityFeed items={activity} />
          </div>
        </GlassPanel>

        <GlassPanel className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/85">Attention</div>
              <div className="text-xs text-white/50">Overdue tasks</div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {recentOverdue.length === 0 ? (
              <EmptyState title="All clear" subtitle="No overdue tasks right now. Keep the pace." />
            ) : (
              recentOverdue.map((t) => (
                <div key={t.id} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-white/90">{t.title}</div>
                      <div className="mt-1 text-xs text-white/50">
                        Due {t.dueDate} • <span className="text-rose-200/80">Overdue</span>
                      </div>
                    </div>
                    <div className="rounded-xl border border-rose-300/20 bg-rose-400/10 px-2 py-1 text-[10px] text-rose-200/90">
                      Urgent
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}

/* =============================================================================
 * Kanban
 * ============================================================================= */

function KanbanBoard({
  columns,
  tasks,
  draggingId,
  onDragStart,
  onDropToStatus,
  onOpenTask,
  onAddColumn,
  onRenameColumn,
  onRemoveColumn,
  onToggleBlocked,
}) {
  const tasksByStatus = useMemo(() => {
    const map = new Map();
    for (const c of columns) map.set(c.id, []);
    for (const t of tasks) {
      const s = map.has(t.status) ? t.status : columns[0]?.id || "backlog";
      map.get(s).push(t);
    }
    // sort: priority then due
    const prRank = { critical: 0, high: 1, medium: 2, low: 3 };
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => {
        const pa = prRank[a.priority] ?? 9;
        const pb = prRank[b.priority] ?? 9;
        if (pa !== pb) return pa - pb;
        const da = a.dueDate || "9999-99-99";
        const db = b.dueDate || "9999-99-99";
        return da.localeCompare(db);
      });
      map.set(k, list);
    }
    return map;
  }, [tasks, columns]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-xs text-white/50">Board</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight">Kanban Flow</div>
          <div className="mt-1 text-sm text-white/60">Drag tasks between columns — buttery smooth.</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onAddColumn}
            className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-2 text-sm text-white/90 hover:bg-white/[0.1]"
          >
            + Add column
          </button>
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/60">
            Tip: drag a task onto Calendar dates too.
          </div>
        </div>
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="min-w-[980px] grid grid-cols-5 gap-4">
          {columns.map((col) => {
            const list = tasksByStatus.get(col.id) || [];
            return (
              <GlassPanel key={col.id} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-white/90 truncate">{col.title}</div>
                      <div className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/60">
                        {list.length}
                      </div>
                    </div>
                    <div className="text-xs text-white/45">Drop zone enabled</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onRenameColumn(col.id)}
                      className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] text-white/70 hover:bg-white/[0.07]"
                      title="Rename"
                    >
                      Edit
                    </button>
                    {col.id !== "backlog" && (
                      <button
                        onClick={() => onRemoveColumn(col.id)}
                        className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] text-white/70 hover:bg-white/[0.07]"
                        title="Remove"
                      >
                        —
                      </button>
                    )}
                  </div>
                </div>

                <div
                  className="mt-3 min-h-[520px] rounded-2xl border border-white/10 bg-white/[0.03] p-2"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropToStatus(col.id)}
                >
                  <motion.div layout className="flex flex-col gap-2">
                    {list.length === 0 ? (
                      <div className="p-3">
                        <EmptyState
                          title="No tasks"
                          subtitle="Drop something here or create new tasks in Notes."
                        />
                      </div>
                    ) : (
                      list.map((t) => (
                        <motion.div key={t.id} layout>
                          <TaskCard
                            task={t}
                            dragging={draggingId === t.id}
                            onDragStart={() => onDragStart(t.id)}
                            onOpen={() => onOpenTask(t.id)}
                            onToggleBlocked={() => onToggleBlocked(t.id)}
                          />
                        </motion.div>
                      ))
                    )}
                  </motion.div>
                </div>
              </GlassPanel>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
 * Tasks Table
 * ============================================================================= */

function TasksTable({
  tasks,
  columns,
  STATUSES,
  STATUS_LABEL,
  PRIORITIES,
  PRIORITY_LABEL,
  query,
  setQuery,
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,
  tagFilter,
  setTagFilter,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
  selected,
  setSelected,
  onOpenTask,
  onInlineUpdate,
  onDelete,
  onBulk,
}) {
  const allSelected = tasks.length > 0 && tasks.every((t) => selected.has(t.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(tasks.map((t) => t.id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectedCount = selected.size;

  const setSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-xs text-white/50">Table</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight">Tasks</div>
          <div className="mt-1 text-sm text-white/60">Search, filter, inline edit, bulk actions.</div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/60">
            Tip: click a row title to open notes.
          </div>
        </div>
      </div>

      <GlassPanel className="p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Field label="Status">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={inputClass()}
              >
                <option value="all">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Priority">
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className={inputClass()}
              >
                <option value="all">All</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Tag contains">
              <input
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                placeholder="e.g. ai"
                className={inputClass()}
              />
            </Field>

            <Field label="Search">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="title, tag, assignee…"
                className={inputClass()}
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/60">
              Selected: <span className="text-white/90">{selectedCount}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={!selectedCount}
                onClick={() => onBulk({ type: "status", patch: { status: "in_progress" }, label: "Set In Progress" })}
                className={btnSmall(!selectedCount)}
              >
                In Progress
              </button>
              <button
                disabled={!selectedCount}
                onClick={() => onBulk({ type: "status", patch: { status: "done" }, label: "Mark Done" })}
                className={btnSmall(!selectedCount)}
              >
                Done
              </button>
              <button
                disabled={!selectedCount}
                onClick={() => onBulk({ type: "tag", patch: { tags: ["roadmap"] }, label: "Tag: roadmap" })}
                className={btnSmall(!selectedCount)}
              >
                Tag roadmap
              </button>
              <button
                disabled={!selectedCount}
                onClick={() => onBulk({ type: "delete", label: "Delete" })}
                className={btnSmall(!selectedCount)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-white/55">
              <tr className="border-b border-white/10">
                <th className="py-3 pr-2">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <ThSort label="Title" active={sortKey === "title"} dir={sortDir} onClick={() => setSort("title")} />
                <ThSort label="Status" active={sortKey === "status"} dir={sortDir} onClick={() => setSort("status")} />
                <ThSort
                  label="Priority"
                  active={sortKey === "priority"}
                  dir={sortDir}
                  onClick={() => setSort("priority")}
                />
                <ThSort
                  label="Due"
                  active={sortKey === "dueDate"}
                  dir={sortDir}
                  onClick={() => setSort("dueDate")}
                />
                <th className="py-3 px-2">Tags</th>
                <ThSort
                  label="Updated"
                  active={sortKey === "updatedAt"}
                  dir={sortDir}
                  onClick={() => setSort("updatedAt")}
                />
                <th className="py-3 pl-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10">
                    <EmptyState
                      title="No matching tasks"
                      subtitle="Adjust filters or create tasks in Notes."
                    />
                  </td>
                </tr>
              ) : (
                tasks.map((t) => (
                  <tr key={t.id} className="border-b border-white/10 hover:bg-white/[0.03]">
                    <td className="py-3 pr-2">
                      <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleOne(t.id)} />
                    </td>
                    <td className="py-3 px-2">
                      <button onClick={() => onOpenTask(t.id)} className="text-white/90 hover:text-white underline underline-offset-4 decoration-white/20">
                        {t.title}
                      </button>
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-white/50">
                        {t.blocked && (
                          <span className="rounded-lg border border-fuchsia-300/20 bg-fuchsia-400/10 px-2 py-0.5 text-fuchsia-200/90">
                            Blocked
                          </span>
                        )}
                        {isOverdue(t) && (
                          <span className="rounded-lg border border-rose-300/20 bg-rose-400/10 px-2 py-0.5 text-rose-200/90">
                            Overdue
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <select
                        value={t.status}
                        onChange={(e) => onInlineUpdate(t.id, { status: e.target.value })}
                        className={inputClass("py-1")}
                      >
                        {columns.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.title}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-2">
                      <select
                        value={t.priority}
                        onChange={(e) => onInlineUpdate(t.id, { priority: e.target.value })}
                        className={inputClass("py-1")}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {PRIORITY_LABEL[p]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-2">
                      <input
                        type="date"
                        value={t.dueDate || ""}
                        onChange={(e) => onInlineUpdate(t.id, { dueDate: e.target.value })}
                        className={inputClass("py-1")}
                      />
                    </td>
                    <td className="py-3 px-2">
                      <InlineTags
                        tags={t.tags || []}
                        onChange={(tags) => onInlineUpdate(t.id, { tags })}
                      />
                    </td>
                    <td className="py-3 px-2 text-xs text-white/55">
                      {new Date(t.updatedAt || t.createdAt).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-3 pl-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onOpenTask(t.id)}
                          className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1 text-xs text-white/80 hover:bg-white/[0.07]"
                        >
                          Open
                        </button>
                        <button
                          onClick={() => onDelete(t.id)}
                          className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1 text-xs text-white/80 hover:bg-white/[0.07]"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </GlassPanel>
    </div>
  );
}

/* =============================================================================
 * Notes Hub + Brain Dump
 * ============================================================================= */

function NotesHub({
  tasks,
  activeTask,
  setActiveTaskId,
  projectNotes,
  setProjectNotes,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  brainDump,
  setBrainDump,
  onBrainDumpConvert,
  newTaskTitleRef,
}) {
  const [quickTitle, setQuickTitle] = useState("");
  const [taskSearch, setTaskSearch] = useState("");

  const list = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    const arr = [...tasks];
    arr.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    if (!q) return arr;
    return arr.filter((t) => `${t.title} ${(t.tags || []).join(" ")}`.toLowerCase().includes(q));
  }, [tasks, taskSearch]);

  const createFromQuick = () => {
    const title = quickTitle.trim();
    if (!title) return;
    const id = onCreateTask(title);
    setQuickTitle("");
    setTimeout(() => {
      // open immediately
      setActiveTaskId(id);
      newTaskTitleRef?.current?.focus?.();
    }, 50);
  };

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <GlassPanel className="p-4 xl:col-span-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-white/85">Tasks</div>
            <div className="text-xs text-white/50">Pick a task to edit notes</div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <input
            value={taskSearch}
            onChange={(e) => setTaskSearch(e.target.value)}
            placeholder="Filter tasks…"
            className={inputClass()}
          />
        </div>

        <div className="mt-3 flex items-center gap-2">
          <input
            ref={newTaskTitleRef}
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            placeholder="Quick create task…"
            className={inputClass()}
            onKeyDown={(e) => {
              if (e.key === "Enter") createFromQuick();
            }}
          />
          <button onClick={createFromQuick} className="rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white/90 hover:bg-white/[0.1]">
            Add
          </button>
        </div>

        <div className="mt-4 max-h-[520px] overflow-auto pr-1">
          {list.length === 0 ? (
            <EmptyState title="No tasks" subtitle="Create one with the quick input." />
          ) : (
            <div className="space-y-2">
              {list.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTaskId(t.id)}
                  className={[
                    "w-full rounded-2xl border p-3 text-left transition",
                    activeTask?.id === t.id
                      ? "border-white/20 bg-white/[0.08]"
                      : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-white/90">{t.title}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {(t.tags || []).slice(0, 3).map((tag) => (
                          <span key={tag} className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/60">
                            {tag}
                          </span>
                        ))}
                        {t.dueDate && (
                          <span className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/60">
                            {t.dueDate}
                          </span>
                        )}
                        {t.blocked && (
                          <span className="rounded-lg border border-fuchsia-300/20 bg-fuchsia-400/10 px-2 py-0.5 text-[10px] text-fuchsia-200/90">
                            Blocked
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-[10px] text-white/45">{new Date(t.updatedAt || t.createdAt).toLocaleDateString()}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </GlassPanel>

      <GlassPanel className="p-4 xl:col-span-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-white/85">Task Notes</div>
            <div className="text-xs text-white/50">Details + notes per task</div>
          </div>
        </div>

        {!activeTask ? (
          <div className="mt-4">
            <EmptyState title="No task selected" subtitle="Pick a task from the left to edit." />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <Field label="Title">
              <input
                value={activeTask.title}
                onChange={(e) => onUpdateTask(activeTask.id, { title: e.target.value })}
                className={inputClass()}
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Status">
                <select
                  value={activeTask.status}
                  onChange={(e) => onUpdateTask(activeTask.id, { status: e.target.value })}
                  className={inputClass()}
                >
                  <option value="backlog">Backlog</option>
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="review">Review</option>
                  <option value="done">Done</option>
                </select>
              </Field>

              <Field label="Priority">
                <select
                  value={activeTask.priority}
                  onChange={(e) => onUpdateTask(activeTask.id, { priority: e.target.value })}
                  className={inputClass()}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </Field>

              <Field label="Due date">
                <input
                  type="date"
                  value={activeTask.dueDate || ""}
                  onChange={(e) => onUpdateTask(activeTask.id, { dueDate: e.target.value })}
                  className={inputClass()}
                />
              </Field>

              <Field label="Assignee (optional)">
                <input
                  value={activeTask.assignee || ""}
                  onChange={(e) => onUpdateTask(activeTask.id, { assignee: e.target.value })}
                  placeholder="e.g. Mohammed"
                  className={inputClass()}
                />
              </Field>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => onUpdateTask(activeTask.id, { blocked: !activeTask.blocked })}
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-xs text-white/85 hover:bg-white/[0.1]"
              >
                {activeTask.blocked ? "Unblock" : "Mark Blocked"}
              </button>

              <button
                onClick={() => onUpdateTask(activeTask.id, { status: "done", blocked: false })}
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-xs text-white/85 hover:bg-white/[0.1]"
              >
                Mark Done
              </button>

              <button
                onClick={() => onDeleteTask(activeTask.id)}
                className="ml-auto rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-xs text-white/85 hover:bg-white/[0.1]"
              >
                Delete task
              </button>
            </div>

            <Field label="Tags">
              <TagsEditor
                tags={activeTask.tags || []}
                onChange={(tags) => onUpdateTask(activeTask.id, { tags })}
              />
            </Field>

            <Field label="Notes">
              <textarea
                value={activeTask.notes || ""}
                onChange={(e) => onUpdateTask(activeTask.id, { notes: e.target.value })}
                rows={10}
                className={textareaClass()}
              />
            </Field>
          </div>
        )}
      </GlassPanel>

      <GlassPanel className="p-4 xl:col-span-3">
        <div className="text-sm text-white/85">Project Notes</div>
        <div className="text-xs text-white/50">Global documentation panel</div>

        <div className="mt-3">
          <textarea
            value={projectNotes}
            onChange={(e) => setProjectNotes(e.target.value)}
            rows={10}
            className={textareaClass()}
          />
        </div>

        <div className="mt-4">
          <div className="text-sm text-white/85">Brain dump → tasks</div>
          <div className="mt-1 text-xs text-white/50">
            One line per task. Optional: <span className="text-white/70">#tag</span>, <span className="text-white/70">@YYYY-MM-DD</span>, <span className="text-white/70">:status</span>, <span className="text-white/70">!high</span>, <span className="text-white/70">[blocked]</span>
          </div>

          <textarea
            value={brainDump}
            onChange={(e) => setBrainDump(e.target.value)}
            rows={8}
            className={textareaClass("mt-3")}
            placeholder={`Example:
Build billing page #saas !high @2026-03-01 :planned
Refactor cache keys #redis #ai !crit [blocked]
`}
          />

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onBrainDumpConvert}
              className="rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-2 text-sm text-white/90 hover:bg-white/[0.12]"
            >
              Convert to tasks
            </button>
            <button
              onClick={() => setBrainDump("")}
              className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-2 text-sm text-white/80 hover:bg-white/[0.08]"
            >
              Clear
            </button>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
}

/* =============================================================================
 * Calendar Planner (Month + Weeks list)
 * ============================================================================= */

function CalendarPlanner({
  calAnchor,
  setCalAnchor,
  weekRows,
  tasksByDate,
  tasks,
  draggingId,
  onDragStart,
  onDropToDate,
  onOpenDay,
  onCreateOnDay,
  onOpenTask,
}) {
  const monthLabel = useMemo(
    () => calAnchor.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
    [calAnchor]
  );

  const inMonth = (d) => d.getMonth() === calAnchor.getMonth();

  const weeklyList = useMemo(() => {
    // Show weeks under the month and list tasks scheduled that week
    const weeks = weekRows.weeks;
    const rows = weeks.map((w) => {
      const start = w[0];
      const end = w[6];
      const isoStart = isoFromDate(start);
      const isoEnd = isoFromDate(end);

      const items = tasks
        .filter((t) => t.dueDate)
        .filter((t) => {
          const dd = parseISODate(t.dueDate);
          if (!dd) return false;
          return dd >= start && dd <= end;
        })
        .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
      return { isoStart, isoEnd, start, end, items };
    });

    // prefer weeks that intersect current month
    return rows.filter((r) => inMonth(r.start) || inMonth(r.end));
  }, [weekRows, tasks, calAnchor]);

  const goMonth = (delta) => {
    const d = new Date(calAnchor);
    d.setMonth(d.getMonth() + delta);
    setCalAnchor(d);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-xs text-white/50">Planner</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight">Calendar</div>
          <div className="mt-1 text-sm text-white/60">Drag tasks onto dates to schedule.</div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => goMonth(-1)} className="rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white/90 hover:bg-white/[0.1]">
            ←
          </button>
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white/90">
            {monthLabel}
          </div>
          <button onClick={() => goMonth(1)} className="rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white/90 hover:bg-white/[0.1]">
            →
          </button>
          <button
            onClick={() => setCalAnchor(new Date())}
            className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-2 text-sm text-white/80 hover:bg-white/[0.08]"
          >
            Today
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <GlassPanel className="p-4 xl:col-span-8">
          <div className="grid grid-cols-7 gap-2 text-xs text-white/55">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="px-2 py-1">{d}</div>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-7 gap-2">
            {weekRows.weeks.flat().map((date) => {
              const iso = isoFromDate(date);
              const list = tasksByDate.get(iso) || [];
              const dim = !inMonth(date);
              const isToday = iso === todayISO();

              return (
                <div
                  key={iso}
                  className={[
                    "min-h-[112px] rounded-2xl border p-2 transition",
                    dim ? "border-white/5 bg-white/[0.02] opacity-80" : "border-white/10 bg-white/[0.03]",
                    "hover:bg-white/[0.05]",
                  ].join(" ")}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropToDate(iso)}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => onOpenDay(iso)}
                      className="text-left text-xs text-white/70 hover:text-white"
                      title="Open day"
                    >
                      <span className={isToday ? "text-cyan-200/90" : ""}>{date.getDate()}</span>
                    </button>

                    <button
                      onClick={() => onCreateOnDay(iso)}
                      className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] text-white/60 hover:bg-white/[0.06]"
                      title="Create task"
                    >
                      +
                    </button>
                  </div>

                  <motion.div layout className="mt-2 space-y-1">
                    {list.slice(0, 3).map((t) => (
                      <motion.div key={t.id} layout>
                        <div
                          draggable
                          onDragStart={() => onDragStart(t.id)}
                          className={[
                            "cursor-grab rounded-xl border px-2 py-1 text-[11px] transition active:cursor-grabbing",
                            draggingId === t.id ? "border-white/25 bg-white/[0.10]" : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
                          ].join(" ")}
                          onClick={() => onOpenTask(t.id)}
                          title={t.title}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="truncate text-white/85">{t.title}</div>
                            {t.blocked && <span className="text-[10px] text-fuchsia-200/90">⛔</span>}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                    {list.length > 3 && (
                      <div className="text-[10px] text-white/45 px-1">+ {list.length - 3} more</div>
                    )}
                  </motion.div>
                </div>
              );
            })}
          </div>
        </GlassPanel>

        <GlassPanel className="p-4 xl:col-span-4">
          <div className="text-sm text-white/85">Weeks</div>
          <div className="text-xs text-white/50">Tasks grouped under each week</div>

          <div className="mt-4 space-y-3 max-h-[760px] overflow-auto pr-1">
            {weeklyList.length === 0 ? (
              <EmptyState title="No weeks" subtitle="Change month or schedule tasks." />
            ) : (
              weeklyList.map((w) => (
                <div key={w.isoStart} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-white/70">
                      {formatNiceDate(w.isoStart)} → {formatNiceDate(w.isoEnd)}
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/60">
                      {w.items.length}
                    </div>
                  </div>

                  <div className="mt-2 space-y-2">
                    {w.items.length === 0 ? (
                      <div className="text-xs text-white/45">No scheduled tasks</div>
                    ) : (
                      w.items.slice(0, 8).map((t) => (
                        <button
                          key={t.id}
                          className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-2 py-2 text-left text-xs text-white/85 hover:bg-white/[0.07]"
                          onClick={() => onOpenTask(t.id)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="truncate">{t.title}</div>
                            <div className="text-[10px] text-white/50">{t.dueDate}</div>
                          </div>
                        </button>
                      ))
                    )}
                    {w.items.length > 8 && (
                      <div className="text-[10px] text-white/45">+ {w.items.length - 8} more</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}

/* =============================================================================
 * Day Drawer
 * ============================================================================= */

function DayDrawer({ open, iso, onClose, tasks, onOpenTask, onCreate }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/60"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: 26, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 26, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed right-3 top-3 bottom-3 z-[80] w-[420px] max-w-[92vw]"
          >
            <GlassPanel className="h-full p-4 flex flex-col">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-white/50">Day</div>
                  <div className="text-lg font-semibold text-white/90">{iso}</div>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2 text-xs text-white/80 hover:bg-white/[0.08]"
                >
                  Close
                </button>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={onCreate}
                  className="rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-2 text-sm text-white/90 hover:bg-white/[0.12]"
                >
                  + Create task
                </button>
              </div>

              <div className="mt-4 flex-1 overflow-auto pr-1 space-y-2">
                {tasks.length === 0 ? (
                  <EmptyState title="No tasks for this day" subtitle="Schedule tasks by dragging them onto this date." />
                ) : (
                  tasks.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => onOpenTask(t.id)}
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-left hover:bg-white/[0.07]"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm text-white/90">{t.title}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            {(t.tags || []).slice(0, 4).map((tag) => (
                              <span key={tag} className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/60">
                                {tag}
                              </span>
                            ))}
                            {t.blocked && (
                              <span className="rounded-lg border border-fuchsia-300/20 bg-fuchsia-400/10 px-2 py-0.5 text-[10px] text-fuchsia-200/90">
                                Blocked
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-[10px] text-white/45">{t.priority}</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </GlassPanel>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* =============================================================================
 * Reusable Components
 * ============================================================================= */

function GlassPanel({ className = "", children }) {
  return (
    <div
      className={[
        "rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-xl",
        "shadow-[0_18px_70px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function SummaryCard({ title, value, glow = "cyan" }) {
  const glowMap = {
    cyan: "from-cyan-300/25 via-transparent to-transparent",
    emerald: "from-emerald-300/25 via-transparent to-transparent",
    indigo: "from-indigo-300/25 via-transparent to-transparent",
    fuchsia: "from-fuchsia-300/25 via-transparent to-transparent",
    rose: "from-rose-300/25 via-transparent to-transparent",
  };
  return (
    <GlassPanel className="relative p-4 overflow-hidden">
      <div className={`pointer-events-none absolute -inset-24 bg-gradient-to-br ${glowMap[glow]} blur-[60px]`} />
      <div className="relative">
        <div className="text-xs text-white/55">{title}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
        <div className="mt-1 text-xs text-white/45">Auto-updating</div>
      </div>
    </GlassPanel>
  );
}

function ActivityFeed({ items }) {
  return (
    <div className="max-h-[320px] overflow-auto pr-1 space-y-2">
      {items.length === 0 ? (
        <EmptyState title="No activity" subtitle="Updates will appear here." />
      ) : (
        items.map((a) => (
          <div key={a.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm text-white/85">{a.message}</div>
                <div className="mt-1 text-xs text-white/45">
                  {new Date(a.ts).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })} • {a.type}
                </div>
              </div>
              <div className="h-2 w-2 rounded-full bg-white/40 mt-1.5" />
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TaskCard({ task, dragging, onDragStart, onOpen, onToggleBlocked }) {
  const pr = task.priority;
  const prBadge =
    pr === "critical"
      ? "border-rose-300/25 bg-rose-400/10 text-rose-200/90"
      : pr === "high"
      ? "border-fuchsia-300/25 bg-fuchsia-400/10 text-fuchsia-200/90"
      : pr === "medium"
      ? "border-white/10 bg-white/[0.04] text-white/70"
      : "border-white/10 bg-white/[0.04] text-white/60";

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={[
        "rounded-2xl border p-3 transition cursor-grab active:cursor-grabbing",
        dragging ? "border-white/25 bg-white/[0.12]" : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2">
        <button onClick={onOpen} className="min-w-0 text-left">
          <div className="truncate text-sm font-semibold text-white/90">{task.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <span className={`rounded-lg border px-2 py-0.5 text-[10px] ${prBadge}`}>{task.priority}</span>
            {task.dueDate && (
              <span className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/60">
                {task.dueDate}
              </span>
            )}
            {isOverdue(task) && (
              <span className="rounded-lg border border-rose-300/20 bg-rose-400/10 px-2 py-0.5 text-[10px] text-rose-200/90">
                Overdue
              </span>
            )}
            {(task.tags || []).slice(0, 3).map((tag) => (
              <span key={tag} className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/60">
                {tag}
              </span>
            ))}
          </div>
        </button>

        <button
          onClick={onToggleBlocked}
          className={[
            "shrink-0 rounded-xl border px-2 py-1 text-[11px] transition",
            task.blocked ? "border-fuchsia-300/25 bg-fuchsia-400/10 text-fuchsia-200/90" : "border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/[0.07]",
          ].join(" ")}
          title="Toggle blocked"
        >
          {task.blocked ? "Blocked" : "Block"}
        </button>
      </div>
      {task.assignee ? <div className="mt-2 text-[11px] text-white/45">Assignee: {task.assignee}</div> : null}
    </div>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-center">
      <div className="text-sm text-white/85">{title}</div>
      <div className="mt-1 text-xs text-white/50">{subtitle}</div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
          <div className="h-4 w-28 rounded bg-white/10" />
          <div className="mt-3 h-8 w-16 rounded bg-white/10" />
          <div className="mt-3 h-2 w-full rounded bg-white/10" />
          <div className="mt-2 h-2 w-5/6 rounded bg-white/10" />
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-white/50 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ThSort({ label, active, dir, onClick }) {
  return (
    <th className="py-3 px-2 cursor-pointer select-none" onClick={onClick}>
      <div className="flex items-center gap-2">
        <span className="text-white/60">{label}</span>
        {active && <span className="text-[10px] text-white/45">{dir === "asc" ? "▲" : "▼"}</span>}
      </div>
    </th>
  );
}

function InlineTags({ tags, onChange }) {
  const [val, setVal] = useState("");

  useEffect(() => {
    setVal((tags || []).join(", "));
  }, [tags]);

  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        const next = val
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 12);
        onChange(next);
      }}
      className={inputClass("py-1")}
      placeholder="tag1, tag2"
      title="Edit tags (comma separated)"
    />
  );
}

function TagsEditor({ tags, onChange }) {
  const [input, setInput] = useState("");
  useEffect(() => setInput((tags || []).join(", ")), [tags]);

  const commit = () => {
    const next = input
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 20);
    onChange(next);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
        placeholder="comma separated tags"
        className={inputClass()}
      />
      <button
        onClick={commit}
        className="rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-xs text-white/85 hover:bg-white/[0.1]"
      >
        Save
      </button>
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1.5">
      <div className="text-[10px] text-white/50">{label}</div>
      <div className="text-sm text-white/90">{value}</div>
    </div>
  );
}

function ShortcutRow({ k, v }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-2 py-1.5">
      <span className="text-white/60">{v}</span>
      <span className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/70">
        {k}
      </span>
    </div>
  );
}

function Pill({ label, hint }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="rounded-lg border border-white/10 bg-white/[0.05] px-2 py-0.5 text-[10px] text-white/80">
          {label}
        </span>
        <span className="text-white/55">{hint}</span>
      </div>
    </div>
  );
}

/* =============================================================================
 * Charts (pure SVG, no deps)
 * ============================================================================= */

function MiniLineChart({ data }) {
  // data: [{x, y}]
  const w = 720;
  const h = 220;
  const pad = 18;

  const ys = data.map((d) => d.y);
  const minY = Math.min(...ys, 0);
  const maxY = Math.max(...ys, 1);

  const xStep = (w - pad * 2) / Math.max(1, data.length - 1);
  const mapX = (i) => pad + i * xStep;
  const mapY = (y) => {
    const t = (y - minY) / Math.max(1e-6, maxY - minY);
    return h - pad - t * (h - pad * 2);
  };

  const d = data
    .map((p, i) => `${i === 0 ? "M" : "L"} ${mapX(i).toFixed(2)} ${mapY(p.y).toFixed(2)}`)
    .join(" ");

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[220px]">
        <defs>
          <linearGradient id="lineGlow" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(34,211,238,0.8)" />
            <stop offset="50%" stopColor="rgba(129,140,248,0.8)" />
            <stop offset="100%" stopColor="rgba(232,121,249,0.8)" />
          </linearGradient>
          <linearGradient id="fillGlow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(129,140,248,0.18)" />
            <stop offset="100%" stopColor="rgba(129,140,248,0)" />
          </linearGradient>
          <filter id="soft">
            <feGaussianBlur stdDeviation="2.4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* grid */}
        {Array.from({ length: 5 }).map((_, i) => {
          const y = pad + (i * (h - pad * 2)) / 4;
          return <line key={i} x1={pad} y1={y} x2={w - pad} y2={y} stroke="rgba(255,255,255,0.06)" />;
        })}

        {/* area */}
        <path
          d={`${d} L ${mapX(data.length - 1)} ${h - pad} L ${mapX(0)} ${h - pad} Z`}
          fill="url(#fillGlow)"
        />

        {/* line */}
        <path d={d} fill="none" stroke="url(#lineGlow)" strokeWidth="3.2" filter="url(#soft)" />

        {/* points */}
        {data.map((p, i) => (
          <circle key={p.x} cx={mapX(i)} cy={mapY(p.y)} r="3.2" fill="rgba(255,255,255,0.65)" />
        ))}
      </svg>

      <div className="mt-2 flex items-center justify-between text-xs text-white/50">
        <span>{data[0]?.x}</span>
        <span>{data[data.length - 1]?.x}</span>
      </div>
    </div>
  );
}

function MiniDonut({ data }) {
  // data: [{label, value}]
  const total = data.reduce((a, b) => a + (b.value || 0), 0) || 1;

  const r = 46;
  const c = 2 * Math.PI * r;
  let acc = 0;

  const colors = [
    "rgba(34,211,238,0.75)",
    "rgba(129,140,248,0.75)",
    "rgba(232,121,249,0.75)",
    "rgba(251,113,133,0.7)",
    "rgba(52,211,153,0.7)",
  ];

  return (
    <div className="flex items-center justify-center">
      <svg width="140" height="140" viewBox="0 0 140 140">
        <defs>
          <filter id="donutSoft">
            <feGaussianBlur stdDeviation="1.8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" />

        {data.map((s, i) => {
          const frac = (s.value || 0) / total;
          const dash = frac * c;
          const gap = c - dash;
          const offset = -acc * c;
          acc += frac;
          return (
            <circle
              key={s.label}
              cx="70"
              cy="70"
              r={r}
              fill="none"
              stroke={colors[i % colors.length]}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              transform="rotate(-90 70 70)"
              filter="url(#donutSoft)"
            />
          );
        })}

        <text x="70" y="68" textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="16" fontWeight="700">
          {Math.round((data.find((d) => d.label === "done")?.value || 0) / total * 100)}%
        </text>
        <text x="70" y="86" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="10">
          done
        </text>
      </svg>
    </div>
  );
}

/* =============================================================================
 * Styling Helpers
 * ============================================================================= */

function inputClass(extra = "") {
  return [
    "w-full rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2 text-sm text-white/90",
    "placeholder:text-white/35 outline-none focus:border-white/20 focus:bg-white/[0.07]",
    extra,
  ].join(" ");
}

function textareaClass(extra = "") {
  return [
    "w-full rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2 text-sm text-white/90",
    "placeholder:text-white/35 outline-none focus:border-white/20 focus:bg-white/[0.07]",
    "min-h-[140px] resize-none",
    extra,
  ].join(" ");
}

function btnSmall(disabled) {
  return [
    "rounded-2xl border px-3 py-2 text-xs transition",
    disabled
      ? "border-white/10 bg-white/[0.03] text-white/35 cursor-not-allowed"
      : "border-white/10 bg-white/[0.06] text-white/85 hover:bg-white/[0.1]",
  ].join(" ");
}

/* =============================================================================
 * Shared date helpers (for Calendar)
 * ============================================================================= */

function pad2(n) {
  return String(n).padStart(2, "0");
}
function isoFromDate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
function parseISODate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function startOfWeek(date, weekStartsOnMonday = true) {
  const d = new Date(date);
  const day = d.getDay();
  const shift = weekStartsOnMonday ? (day === 0 ? 6 : day - 1) : day;
  d.setDate(d.getDate() - shift);
  d.setHours(0, 0, 0, 0);
  return d;
}
function formatNiceDate(iso) {
  const d = parseISODate(iso);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function isOverdue(task) {
  if (!task?.dueDate) return false;
  if (task?.status === "done") return false;
  const d = parseISODate(task.dueDate);
  if (!d) return false;
  const now = new Date();
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const b = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return b < a;
}
